/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Button, Icon } from '@mairistem/composants';
import { request } from '@mairistem/tools';

import { BASE_URL_ETATCIVIL, COLORS } from '@/constants';
import type { Mention, OptionCodeAnts } from '@/types';
import { AssistantContext } from '@/contexts';

import MentionForm from './MentionForm';
import MentionCard from './MentionCard';

const URL_MENTION_ANTS = `${BASE_URL_ETATCIVIL}/mention-ants`;

const StepMentions = () => {
  /** Liste des mentions saisies (la plus récente en premier) */
  const { mentions, setMentions } = React.useContext(AssistantContext);
  /** Affichage du formulaire de saisie d'une nouvelle mention (ouvert à l'arrivée) */
  const [isFormOpen, setIsFormOpen] = React.useState<boolean>(true);
  /** Options du dropdown « Code ANTS » (chargées depuis l'API). */
  const [optionsCodeAnts, setOptionsCodeAnts] = React.useState<
    OptionCodeAnts[]
  >([]);

  React.useEffect(() => {
    request.get(URL_MENTION_ANTS).then(({ data }) => {
      // La route renvoie des CATÉGORIES contenant chacune des mentions : on aplatit
      // sur les mentions, car c'est leur identifiant que le back attend
      // (`identifiantCodeAnts`), pas celui de la catégorie.
      setOptionsCodeAnts(
        _.flatMap(data, (categorie) =>
          _.map(categorie.mentions, (mentionAnts) => ({
            key: mentionAnts.identifiant,
            value: mentionAnts.identifiant,
            text: `${categorie.codeRefAnts} - ${categorie.type} - ${mentionAnts.intitule}`,
          })),
        ),
      );
    });
  }, []);

  const handleOpenForm = React.useCallback(() => {
    setIsFormOpen(true);
  }, []);

  const handleCancel = React.useCallback(() => {
    setIsFormOpen(false);
  }, []);

  const handleValidate = React.useCallback(
    (mention: Omit<Mention, 'id'>) => {
      setMentions([{ ...mention, id: _.uniqueId('mention-') }, ...mentions]);
      setIsFormOpen(false);
    },
    [setMentions, mentions],
  );

  return (
    <div
      className="assistant-step-mentions"
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      {!isFormOpen && (
        <Button
          basic
          data-testid="mention-ajouter"
          onClick={handleOpenForm}
          style={{
            color: COLORS.Background,
            borderColor: COLORS.Background,
            boxShadow: `0 0 0 1px ${COLORS.Background} inset`,
            flexShrink: 0,
          }}
        >
          <Icon name="plus" />
          Ajouter une mention
        </Button>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75em',
          width: '100%',
          marginTop: '0.75em',
        }}
      >
        {isFormOpen && (
          <MentionForm
            onValidate={handleValidate}
            onCancel={handleCancel}
            numerosExistants={_.map(mentions, (mention) => mention.numero)}
            optionsCodeAnts={optionsCodeAnts}
          />
        )}
        {_.map(mentions, (mention) => (
          <MentionCard
            key={mention.id}
            mention={mention}
            optionsCodeAnts={optionsCodeAnts}
          />
        ))}
      </div>
    </div>
  );
};

export default StepMentions;
